import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { type PdfExportOptions, type PdfFooterConfig, type PdfHeaderConfig } from "src/hooks/usePdfExport";

export const getPublicationDescription = (page: PuckPage): string => {
    if (!page.content) return "";
    try {
        const data = JSON.parse(page.content);
        const description = data?.root?.props?.description;
        if (typeof description === "string" && description.trim() !== "") return description;

        return "";
    } catch {
        return "";
    }
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const isEnabledFlag = (value: unknown): boolean => value === true || value === "true" || value === 1 || value === "1";

const mmToMarginValue = (v: unknown): string | undefined => {
    if (typeof v !== "number" || Number.isNaN(v)) return undefined;
    return `${v}mm`;
};

const extractHeaderConfig = (rootProps: Record<string, unknown>): PdfHeaderConfig | undefined => {
    const pdfHeader = rootProps.pdfHeader;
    if (!isRecord(pdfHeader) || !isEnabledFlag(pdfHeader.enabled)) return undefined;

    return {
        enabled: true,
        logoUrl: typeof pdfHeader.logoUrl === "string" ? pdfHeader.logoUrl : undefined,
        logoHeight: typeof pdfHeader.logoHeight === "number" ? pdfHeader.logoHeight : undefined,
        title: typeof pdfHeader.title === "string" ? pdfHeader.title : undefined,
        subtitle: typeof pdfHeader.subtitle === "string" ? pdfHeader.subtitle : undefined,
        alignment:
            ["left", "center", "right"].includes(pdfHeader.alignment as string) ?
                (pdfHeader.alignment as PdfHeaderConfig["alignment"])
            :   undefined,
    };
};

const extractFooterConfig = (rootProps: Record<string, unknown>): PdfFooterConfig | undefined => {
    const pdfFooter = rootProps.pdfFooter;
    if (!isRecord(pdfFooter) || !isEnabledFlag(pdfFooter.enabled)) return undefined;

    return {
        enabled: true,
        showPageNumbers: pdfFooter.showPageNumbers !== false,
        pageNumberFormat: pdfFooter.pageNumberFormat === "page" ? "page" : "pageOfTotal",
        text: typeof pdfFooter.text === "string" ? pdfFooter.text : undefined,
        alignment:
            ["left", "center", "right", "spread"].includes(pdfFooter.alignment as string) ?
                (pdfFooter.alignment as PdfFooterConfig["alignment"])
            :   undefined,
    };
};

export const getExportOptionsFromTemplate = (page: PuckPage): PdfExportOptions => {
    const fallback: PdfExportOptions = {
        format: "A3",
        landscape: false,
        printBackground: true,
        waitForTimeout: 60000,
    };

    if (!page.content) return fallback;

    try {
        const data = JSON.parse(page.content) as unknown;
        if (!isRecord(data)) return fallback;
        const root = (data as { root?: unknown }).root;
        if (!isRecord(root)) return fallback;
        const rootProps = (root as { props?: unknown }).props;
        if (!isRecord(rootProps)) return fallback;

        const pdfHeader = extractHeaderConfig(rootProps);
        const pdfFooter = extractFooterConfig(rootProps);

        const pdfSettings = (rootProps as Record<string, unknown>).pdfSettings;
        if (isRecord(pdfSettings)) {
            const landscape = pdfSettings.landscape === "landscape";

            const marginsMm = (pdfSettings as { marginsMm?: unknown }).marginsMm;
            const margin =
                isRecord(marginsMm) ?
                    {
                        top: mmToMarginValue(marginsMm.top),
                        right: mmToMarginValue(marginsMm.right),
                        bottom: mmToMarginValue(marginsMm.bottom),
                        left: mmToMarginValue(marginsMm.left),
                    }
                :   undefined;

            const hasMargin =
                margin !== undefined &&
                (margin.top !== undefined ||
                    margin.right !== undefined ||
                    margin.bottom !== undefined ||
                    margin.left !== undefined);

            return {
                ...fallback,
                format: "A3",
                landscape,
                margin: hasMargin ? margin : undefined,
                pdfHeader,
                pdfFooter,
            };
        }

        const legacyPdfOptions = (rootProps as Record<string, unknown>).pdfOptions;
        if (isRecord(legacyPdfOptions)) {
            const legacyMargin = (legacyPdfOptions as Record<string, unknown>).margin;
            const margin =
                isRecord(legacyMargin) ?
                    {
                        top: typeof legacyMargin.top === "string" ? legacyMargin.top : undefined,
                        right: typeof legacyMargin.right === "string" ? legacyMargin.right : undefined,
                        bottom: typeof legacyMargin.bottom === "string" ? legacyMargin.bottom : undefined,
                        left: typeof legacyMargin.left === "string" ? legacyMargin.left : undefined,
                    }
                :   undefined;

            const hasMargin =
                margin !== undefined &&
                (margin.top !== undefined ||
                    margin.right !== undefined ||
                    margin.bottom !== undefined ||
                    margin.left !== undefined);

            return {
                ...fallback,
                format: "A3",
                landscape:
                    typeof legacyPdfOptions.landscape === "boolean" ? legacyPdfOptions.landscape : fallback.landscape,
                printBackground:
                    typeof legacyPdfOptions.printBackground === "boolean" ?
                        legacyPdfOptions.printBackground
                    :   fallback.printBackground,
                waitForTimeout:
                    typeof legacyPdfOptions.waitForTimeout === "number" ?
                        legacyPdfOptions.waitForTimeout
                    :   fallback.waitForTimeout,
                scale: typeof legacyPdfOptions.scale === "number" ? legacyPdfOptions.scale : fallback.scale,
                margin: hasMargin ? margin : undefined,
                pdfHeader,
                pdfFooter,
            };
        }

        return {
            ...fallback,
            pdfHeader,
            pdfFooter,
        };
    } catch {
        return fallback;
    }
};
